import { useState } from 'react';
import { api } from '../lib/api';
import { Empty, ErrorNote, Loading, useResource, useToast } from './ui';

interface AttachmentRow {
  id: string; filename: string; mime: string; sizeBytes: number; sha256: string;
  note: string; uploadedBy: string; uploadedAt: string;
}

const size = (n: number) => (n < 1024 ? `${n} B` : n < 1_048_576 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1_048_576).toFixed(1)} MB`);

function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = () => reject(new Error('Could not read the file.'));
    r.readAsDataURL(file);
  });
}

/** Evidence files on a rights request, a processor or a guardian check, with the digest taken at upload. */
export function Attachments({ entityType, entityId, canAdd }: { entityType: 'request' | 'processor' | 'guardian'; entityId: string; canAdd: boolean }) {
  const toast = useToast();
  const [tick, setTick] = useState(0);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const files = useResource(() => api.get<{ attachments: AttachmentRow[] }>(`/v1/attachments?entityType=${entityType}&entityId=${entityId}`), [entityType, entityId, tick]);

  async function upload(file: File | undefined) {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast('Files are limited to 10 MB.', 'bad'); return; }
    setBusy(true);
    try {
      const mime = file.type || (file.name.endsWith('.eml') ? 'message/rfc822' : file.name.endsWith('.msg') ? 'application/vnd.ms-outlook' : 'application/octet-stream');
      await api.post('/v1/attachments', { entityType, entityId, filename: file.name, mime, contentBase64: await toBase64(file), note: note || undefined });
      setNote('');
      setTick((t) => t + 1);
      toast('File added.', 'ok');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  const rows = files.data?.attachments ?? [];
  return (
    <div>
      {files.loading && !files.data && <Loading />}
      <ErrorNote error={files.error} />
      {files.data && rows.length === 0 && <Empty>No files.</Empty>}
      {rows.length > 0 && (
        <table className="data">
          <thead><tr><th>File</th><th>Added</th><th>SHA-256</th><th /></tr></thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id}>
                <td>{a.filename}<div className="small muted">{size(a.sizeBytes)}{a.note ? ` · ${a.note}` : ''}</div></td>
                <td className="small">{new Date(a.uploadedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}<div className="muted">{a.uploadedBy}</div></td>
                <td className="small mono" title={a.sha256}>{a.sha256.slice(0, 12)}…</td>
                <td className="u-actions"><button className="small" onClick={() => api.download(`/v1/attachments/${a.id}/download`, a.filename).catch((e) => toast((e as Error).message, 'bad'))}>Download</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {canAdd && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" style={{ flex: '1 1 200px' }} />
          <label className="btn small" style={{ cursor: busy ? 'progress' : 'pointer' }}>
            {busy ? 'Uploading…' : 'Add file'}
            <input type="file" hidden disabled={busy} accept=".pdf,.png,.jpg,.jpeg,.txt,.eml,.msg,.doc,.docx,.xls,.xlsx,.csv" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />
          </label>
        </div>
      )}
    </div>
  );
}
